import { useCallback, useEffect, useMemo, useState } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { handleJoinErrorReply } from '@liveHelper/context/VisitorChannelContext/handleJoinErrorReply'
import { useInitialDocumentTitle } from '@liveHelper/hooks/useInitialDocumentTitle'
import { useVisitorTopic } from '@liveHelper/hooks/useVisitorTopic'
import {
  actionsSelector,
  isVisitorOnCallSelector,
  paramsSelector,
  useStore,
  useStoreApi,
} from '@liveHelper/models/store'
import {
  useTrackNavigateOnCall,
  useTrackVisitorJoinFailed,
  useTrackVisitorJoinSucceeded,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { useSocketContext } from '@oneToOne/shared/contexts/SocketContext'

import { useDetectActivity } from '@core/activityDetector/useDetectActivity'
import { customEvent, EventType as CoreEventType } from '@core/customEvent'
import { useIsFocusedTab } from '@core/hooks/useIsFocusedTab'
import { logWarning } from '@core/logging'
import { useChannel } from '@core/phoenix'
import type { UseChannelProps } from '@core/phoenix/hooks/useChannel'
import { getSession } from '@core/tracking/session'

import { handleJoinSuccessReply } from './handleJoinSuccessReply'
import type { PushEvent, ServerEvent, VisitorChannel } from './types'
import { useHandleVisitorMessages } from './useHandleVisitorMessages'
import { useVideoCallLock } from './useVideoCallLock'
import { useVisitorChannelLock } from './useVisitorChannelLock'

/**
 * Hook that creates and joins the visitor channel.
 * @returns The channel instance
 * @private Private to VisitorChannelContext
 */
export function useVisitorChannel(): VisitorChannel | undefined {
  const params = useStore(paramsSelector)
  const chatChannelConfig = useChatChannelConfig()
  const { socket } = useSocketContext()
  const topic = useVisitorTopic()
  const isFocusedTab = useIsFocusedTab()
  const isVisitorOnCall = useStore(isVisitorOnCallSelector)
  const isActive = useDetectActivity() || isVisitorOnCall
  const initialTitle = useInitialDocumentTitle()
  const hasVideoCallLock = useVideoCallLock()
  const { lang, bypass_sa_availability } = useStore(paramsSelector)
  const storeApi = useStoreApi()
  const trackVisitorJoinSucceeded = useTrackVisitorJoinSucceeded()
  const trackVisitorJoinFailed = useTrackVisitorJoinFailed()
  const trackNavigateOnCall = useTrackNavigateOnCall()
  const { appointment } = useAppointment()
  const session = getSession()
  const sessionId = session?.id

  const shouldRequestVisitorLock = !!(
    topic &&
    (isFocusedTab || hasVideoCallLock) &&
    isActive &&
    initialTitle
  )
  const releaseVisitorLock = useVisitorChannelLock(shouldRequestVisitorLock)
  const handleMessages = useHandleVisitorMessages()
  const hasVisitorLock = !!releaseVisitorLock
  const isWidgetHidden =
    Boolean(chatChannelConfig?.config?.widget_availability === 'never') &&
    !appointment &&
    !params.bypass_widget_availability_hidden

  const [shouldJoin, setShouldJoin] = useState(false)

  useEffect(() => {
    if (chatChannelConfig?.config?.widget_availability === 'never') {
      customEvent({
        type: CoreEventType.FW_LIVE_HELPER_READY,
        data: { widget_id: params.widget_id },
      })
    }
  }, [chatChannelConfig?.config?.widget_availability, params.widget_id])

  // Show warning if title is not set after 5 seconds so integrators can fix it.
  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      if (!initialTitle) {
        logWarning('Document title must be set to use FW live helper.')
      }
    }, 5000)
    return () => clearTimeout(timeoutRef)
  }, [initialTitle])

  const onJoinSuccess = useCallback(
    (response: one_to_one.visitor.ChannelJoinSuccessResponse) => {
      handleJoinSuccessReply({
        response,
        storeApi,
        trackNavigateOnCall,
        chatChannelConfig,
        appointment,
      })
      trackVisitorJoinSucceeded({
        waitlist_capacity_reached: !!response.waitlist_capacity_reached,
      })
    },
    [
      storeApi,
      trackNavigateOnCall,
      chatChannelConfig,
      appointment,
      trackVisitorJoinSucceeded,
    ],
  )
  const onJoinError = useCallback(
    (response: one_to_one.visitor.ChannelJoinErrorResponse) => {
      handleJoinErrorReply({ response, storeApi, chatChannelConfig })
      trackVisitorJoinFailed({
        reason: response.reason ?? 'REASON_UNSPECIFIED',
      })
    },
    [storeApi, chatChannelConfig, trackVisitorJoinFailed],
  )

  const onClose = useCallback(
    (manual?: boolean) => {
      // Manual close happens during controlled cleanup (e.g. temporary
      // focus/lock changes). Avoid resetting UI state in this path so the
      // widget doesn't disappear while user is in a permission prompt flow.
      if (!manual) {
        actionsSelector(storeApi.getState()).resetState()
      }
      releaseVisitorLock?.()
    },
    [releaseVisitorLock, storeApi],
  )

  const payload: UseChannelProps<PushEvent, ServerEvent>['payload'] = useMemo(
    () => ({
      page_title: initialTitle,
      page_url: window.location.href,
      locale: lang,
      bypass_sa_availability,
      session_id: sessionId,
    }),
    [initialTitle, lang, bypass_sa_availability, sessionId],
  )

  //TODO(OTO-1996): We can remove updating the payload by removing title and url
  useEffect(() => {
    if (payload) {
      setTimeout(() => {
        setShouldJoin(
          shouldRequestVisitorLock &&
            hasVisitorLock &&
            !isWidgetHidden &&
            !!chatChannelConfig,
        )
      }, 0)
    }
  }, [
    hasVisitorLock,
    payload,
    shouldRequestVisitorLock,
    isWidgetHidden,
    chatChannelConfig,
  ])

  useEffect(() => {
    return () => {
      // if lock was acquired, release it when this hook is unmounted
      releaseVisitorLock?.()
    }
  }, [releaseVisitorLock])

  const channel = useChannel<PushEvent, ServerEvent>({
    socket,
    topic,
    payload,
    shouldJoin,
    onClose,
    onJoinSuccess,
    onJoinError,
    handleMessages,
  })

  return channel
}
